import { StandardEvents } from '@shopify/events';
import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();
    if (this.details.dataset.productDataRow) {
      this.hidden = this.details.dataset.productDataEmpty === 'true';
      this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#updateProductData, { signal });
    }

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('click', this.#handleContentClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  #dataVersion = 0;

  #updateProductData = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const version = ++this.#dataVersion;
    event.promise?.then(({ detail }) => {
      if (version !== this.#dataVersion || !detail?.html) return;
      const current = this.details;
      const next = detail.html.querySelector(`[data-product-data-row="${CSS.escape(current.dataset.productDataRow)}"]`);
      if (!next) return;
      if (detail.productId && !detail.newProduct && String(detail.productId) !== current.dataset.productId) return;
      const content = current.querySelector('.details-content');
      const nextContent = next.querySelector('.details-content');
      if (content && nextContent) content.replaceChildren(...Array.from(nextContent.childNodes).map(node => node.cloneNode(true)));
      current.dataset.productId = next.dataset.productId;
      current.dataset.productDataEmpty = next.dataset.productDataEmpty;
      current.hidden = next.dataset.productDataEmpty === 'true';
      this.hidden = current.hidden;
    }).catch(error => { if (error?.name !== 'AbortError') console.warn('[accordion] Product details could not update', error); });
  };

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Closes the accordion when a click lands inside an element marked with
   * `[data-accordion-close]` (or one of its descendants). Used by markup that
   * provides an explicit close-target region inside the content area.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleContentClick = (event) => {
    if (!this.details.open) return;

    if (!(/** @type {Element} */ (event.target).closest('[data-accordion-close]'))) return;

    this.summary.focus({ preventScroll: true });
    this.details.open = false;
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.summary.focus();
      this.details.open = false;
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}

// Refresh native product text blocks with the HTML already requested by Tinker's variant picker.
const productFactVersions = new WeakMap();
document.addEventListener(StandardEvents.productSelect, (event) => {
  if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
  const section = event.target.closest('.shopify-section');
  if (!section?.querySelector('[data-product-fact]')) return;
  const version = (productFactVersions.get(section) || 0) + 1;
  productFactVersions.set(section, version);
  event.promise?.then(({ detail }) => {
    if (!detail?.html || productFactVersions.get(section) !== version || !section.isConnected) return;
    section.querySelectorAll('[data-product-fact]').forEach(current => {
      if (detail.productId && !detail.newProduct && String(detail.productId) !== current.dataset.productId) return;
      const next = detail.html.querySelector('[data-product-fact="' + CSS.escape(current.dataset.productFact) + '"]');
      if (next) current.replaceWith(next.cloneNode(true));
    });
  }).catch(error => { if (error?.name !== 'AbortError') console.warn('[product-facts] Details could not update', error); });
});
